import React, { useEffect, useRef, useCallback, useState } from 'react';
import { API_URL, ARTIFACT_CONFIG } from '../config';

type Artifact = {
  type: 'image' | 'video' | 'website' | 'instagram' | 'welcome';
  url: string;
  id?: string;
  session_id?: string;
  product_id?: string;
  created_at?: string;
  user_name?: string;
  product_name?: string;
  product_category?: string;
  product_price?: string;
  product_description?: string;
  product_description_first_paragraph?: string;
};

const IMAGE_DURATION = ARTIFACT_CONFIG.IMAGE_DURATION;
const API_BASE_URL = API_URL;
const BATCH_SIZE = ARTIFACT_CONFIG.BATCH_SIZE;

// Fallback artifacts in case API fails
const FALLBACK_ARTIFACTS: Artifact[] = [
];

const styles = `
.artifact-theater * {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}
.artifact-theater {
  color-scheme: light;
  --ink: #14140F;
  --paper: #FCFAF6;
  --rule: #E4DFD2;
  --muted: #78776E;
  --teal: #0BB8CB;
  --teal-ink: #05606E;
  --coral: #FD8490;
  background:
    radial-gradient(circle at 88% 8%, rgba(11, 184, 203, 0.14), transparent 46%),
    radial-gradient(circle at 6% 94%, rgba(253, 132, 144, 0.13), transparent 46%),
    var(--paper);
  height: 100vh;
  width: 100vw;
  overflow: hidden;
  position: fixed;
  top: 0; left: 0;
  z-index: 100;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
  color: var(--ink);
}
.artifact-theater .theater-container {
  width: 100vw;
  height: 100vh;
  position: relative;
}
.artifact-theater .slide {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 3s ease-in-out;
  top: 0; left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}
.artifact-theater .slide.active {
  opacity: 1;
  z-index: 2;
}
.artifact-theater .slide img,
.artifact-theater .slide video,
.artifact-theater .slide iframe {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: var(--paper);
  display: block;
  border: none;
}

/* Editorial spread: plate on the left, caption block on the right */
.artifact-theater .image-content {
  display: grid;
  grid-template-columns: 1fr 1fr;
  width: 100%;
  height: 100%;
}
.artifact-theater .image-left {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 5vh 3vw 5vh 5vw;
}
.artifact-theater .image-left img {
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
  object-fit: contain;
  background: transparent;
}
.artifact-theater .image-right {
  position: relative;
  height: 100%;
  padding: 8vh 6vw 8vh 4vw;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 22px;
  border-left: 1px solid var(--rule);
}
.artifact-theater .image-right::before {
  content: '';
  position: absolute;
  left: -2px;
  top: 22%;
  bottom: 22%;
  width: 4px;
  background: linear-gradient(180deg, var(--teal), var(--coral));
}
.artifact-theater .designer-name {
  display: flex;
  align-items: center;
  gap: 10px;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--teal-ink);
}
.artifact-theater .designer-name::before {
  content: '';
  width: 22px;
  height: 2px;
  background: linear-gradient(90deg, var(--teal), var(--coral));
}
.artifact-theater .product-name {
  font-family: 'Syne', sans-serif;
  font-size: clamp(38px, 4.4vw, 76px);
  font-weight: 700;
  line-height: 0.98;
  letter-spacing: -0.035em;
}
.artifact-theater .product-category {
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--muted);
  padding-bottom: 20px;
  border-bottom: 1px solid var(--rule);
}
.artifact-theater .product-price {
  font-family: 'Syne', sans-serif;
  font-size: clamp(26px, 2.6vw, 40px);
  font-weight: 700;
  letter-spacing: -0.03em;
  background: linear-gradient(110deg, var(--teal), var(--coral));
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  width: fit-content;
}
.artifact-theater .product-description {
  font-family: 'Inter', sans-serif;
  font-size: clamp(15px, 1.15vw, 19px);
  font-weight: 400;
  line-height: 1.65;
  color: #4A4A44;
  max-width: 54ch;
}
.artifact-theater .video-user-display {
  position: absolute;
  bottom: 32px;
  left: 32px;
  padding: 10px 18px;
  background: linear-gradient(120deg, rgba(5, 96, 110, 0.92), rgba(11, 184, 203, 0.88));
  color: #fff;
  font-family: 'Inter', sans-serif;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  backdrop-filter: blur(6px);
  z-index: 10;
}
.artifact-theater .theater-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 22px;
  text-align: center;
}
.artifact-theater .theater-state img {
  width: 170px;
  height: auto;
}
.artifact-theater .theater-state-title {
  font-family: 'Syne', sans-serif;
  font-size: 26px;
  font-weight: 700;
  letter-spacing: -0.03em;
}
.artifact-theater .theater-state-text {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--teal-ink);
}
.artifact-theater .theater-spinner {
  width: 28px;
  height: 28px;
  border: 2.5px solid rgba(11, 184, 203, 0.2);
  border-top-color: var(--teal);
  border-radius: 50%;
  animation: theater-spin 0.7s linear infinite;
}
@keyframes theater-spin { 100% { transform: rotate(360deg); } }

@media (max-width: 900px) {
  .artifact-theater .image-content { grid-template-columns: 1fr; grid-template-rows: 1fr auto; }
  .artifact-theater .image-right { border-left: 0; border-top: 1px solid var(--rule); padding: 24px; gap: 14px; }
  .artifact-theater .image-left { padding: 24px; }
}
`;

const ArtifactApp: React.FC = () => {
  const [artifactQueue, setArtifactQueue] = useState<Artifact[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [totalAssets, setTotalAssets] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const timeoutRef = useRef<number | null>(null);
  const slidesRef = useRef<(HTMLDivElement | null)[]>([]);
  const videoPlayRequested = useRef(false);
  const isFetchingRef = useRef(false);
  const currentVideoRef = useRef<HTMLVideoElement | null>(null);

  // Fetch artifacts from API
  const fetchArtifacts = useCallback(async (limit: number = BATCH_SIZE) => {
    if (isFetchingRef.current) {
      console.log('🔄 Already fetching, skipping duplicate request');
      return [];
    }

    isFetchingRef.current = true;
    try {
      console.log(`🎭 Fetching ${limit} artifacts (prioritized by display count)`);

      const superpossibleKey = localStorage.getItem('superpossible_key');
      if (!superpossibleKey) {
        throw new Error('Superpossible key not found');
      }
      const response = await fetch(`${API_BASE_URL}/api/artifacts?limit=${limit}&superpossible_key=${superpossibleKey}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.artifacts && data.artifacts.length > 0) {
        console.log(`✅ Fetched ${data.artifacts.length} artifacts from API`);
        console.log(`📊 Total assets in DB: ${data.total_assets}`);
        
        // Update total assets count if provided
        if (data.total_assets !== undefined) {
          setTotalAssets(data.total_assets);
        }
        
        return data.artifacts;
      } else {
        console.log('⚠️ No artifacts returned from API, using fallback');
        return [];
      }
    } catch (error) {
      console.error('❌ Error fetching artifacts:', error);
      return [];
    } finally {
      isFetchingRef.current = false;
    }
  }, []);

  // Load initial artifacts
  useEffect(() => {
    const loadInitialArtifacts = async () => {
      setIsLoading(true);
      
      const artifacts = await fetchArtifacts(BATCH_SIZE);
      
      if (artifacts.length > 0) {
        setArtifactQueue(artifacts);
      } else {
        // Use fallback if API fails
        console.log('🔄 Using fallback artifacts');
        setArtifactQueue(FALLBACK_ARTIFACTS);
      }
      
      setIsLoading(false);
    };

    loadInitialArtifacts();
  }, [fetchArtifacts]);

  // Refetch more artifacts when queue gets low
  const refetchArtifacts = useCallback(async () => {
    if (artifactQueue.length <= 1) { // When only 1 left, fetch more
      console.log('🔄 Queue running low, fetching more artifacts...');
      console.log(`📊 Current state - Queue: ${artifactQueue.length}, Total: ${totalAssets}`);
      
      const newArtifacts = await fetchArtifacts(BATCH_SIZE);
      
      if (newArtifacts.length > 0) {
        setArtifactQueue(prev => [...prev, ...newArtifacts]);
        console.log(`✅ Added ${newArtifacts.length} artifacts to queue`);
      } else {
        console.log('⚠️ No new artifacts available');
      }
    }
  }, [artifactQueue.length, totalAssets, fetchArtifacts]);

  // Clean up current media elements
  const cleanupCurrentMedia = useCallback(() => {
    // Clear any existing timeouts
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    
    // Stop and reset current video if exists
    if (currentVideoRef.current) {
      currentVideoRef.current.pause();
      currentVideoRef.current.currentTime = 0;
      currentVideoRef.current.onended = null;
      currentVideoRef.current.onloadeddata = null;
      currentVideoRef.current = null;
    }
    
    // Clean up all slide references and remove active classes
    slidesRef.current.forEach((slide, idx) => {
      if (slide) {
        slide.classList.remove('active');
        const video = slide.querySelector('video') as HTMLVideoElement | null;
        if (video) {
          video.pause();
          video.currentTime = 0;
          video.onended = null;
          video.onloadeddata = null;
        }
      }
    });
  }, []);

  // Clean up on unmount
  useEffect(() => {
    return () => {
      cleanupCurrentMedia();
    };
  }, [cleanupCurrentMedia]);

  // Helper to go to next slide and manage queue
  const nextSlide = useCallback(() => {
    if (artifactQueue.length === 0 || isTransitioning) return;
    
    setIsTransitioning(true);
    
    // Clean up current media
    cleanupCurrentMedia();
    
    // Small delay to ensure cleanup is complete
    setTimeout(() => {
      // Remove the current artifact from the queue
      setArtifactQueue(prev => prev.slice(1));
      
      // Reset current index since we're removing the first item
      setCurrentIndex(0);
      
      // Reset transition flag
      setIsTransitioning(false);
      
      // Trigger refetch if needed
      refetchArtifacts();
    }, 100);
  }, [artifactQueue.length, isTransitioning, refetchArtifacts, cleanupCurrentMedia]);

  // Handle slide transitions
  useEffect(() => {
    if (isLoading || artifactQueue.length === 0 || isTransitioning) return;

    // Clean up any existing media first
    cleanupCurrentMedia();
    
    // Small delay to ensure DOM is ready
    const setupTimeout = setTimeout(() => {
      // Get current slide (always index 0 since we remove completed artifacts)
      const currentSlide = slidesRef.current[0];
      if (!currentSlide) return;
      
      // Ensure only current slide is active
      slidesRef.current.forEach((slide, idx) => {
        if (slide) {
          if (idx === 0) {
            slide.classList.add('active');
          } else {
            slide.classList.remove('active');
          }
        }
      });

      // Handle media logic for the current artifact in queue
      const currentArtifact = artifactQueue[0];
      if (!currentArtifact) return;

      if (currentArtifact.type === 'video') {
        const video = currentSlide.querySelector('video') as HTMLVideoElement | null;
        if (video) {
          currentVideoRef.current = video;
          video.currentTime = 0;
          video.muted = true; // Ensure muted for autoplay
          
          // Set up video end handler
          video.onended = nextSlide;
          
          // Try to play video
          const playPromise = video.play();
          if (playPromise && typeof playPromise.then === 'function') {
            playPromise.catch(() => {
              // If autoplay fails, fallback to next slide after 5s
              timeoutRef.current = window.setTimeout(nextSlide, 5000);
            });
          }
        }
      } else {
        // Image, Website, and Instagram: advance after IMAGE_DURATION
        timeoutRef.current = window.setTimeout(nextSlide, IMAGE_DURATION);
      }
    }, 50);

    // Cleanup function
    return () => {
      clearTimeout(setupTimeout);
      cleanupCurrentMedia();
    };
  }, [artifactQueue, isLoading, isTransitioning, nextSlide, cleanupCurrentMedia]);

  // On first user click, try to play video if needed (for autoplay policies)
  useEffect(() => {
    const handleFirstClick = () => {
      if (videoPlayRequested.current || artifactQueue.length === 0) return;
      videoPlayRequested.current = true;
      if (currentVideoRef.current) {
        currentVideoRef.current.play().catch(() => {
          // Ignore play errors on user interaction
        });
      }
    };
    window.addEventListener('click', handleFirstClick, { once: true });
    return () => {
      window.removeEventListener('click', handleFirstClick);
    };
  }, [artifactQueue.length]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="artifact-theater">
        <style>{styles}</style>
        <div className="theater-container theater-state">
          <img src="/superpossible_logo.svg" alt="SuperPossible" />
          <span className="theater-spinner" role="status" aria-label="Loading" />
          <p className="theater-state-text">Loading the gallery</p>
        </div>
      </div>
    );
  }

  // Show empty state if no artifacts
  if (artifactQueue.length === 0) {
    return (
      <div className="artifact-theater">
        <style>{styles}</style>
        <div className="theater-container theater-state">
          <img src="/superpossible_logo.svg" alt="SuperPossible" />
          <p className="theater-state-title">The gallery is empty</p>
          <p className="theater-state-text">Generated products and films appear here</p>
        </div>
      </div>
    );
  }

  // Render slides - only render up to BATCH_SIZE artifacts from the queue to keep DOM manageable
  const visibleArtifacts = artifactQueue.slice(0, BATCH_SIZE);
  
  return (
    <div className="artifact-theater">
      <style>{styles}</style>
      <div className="theater-container">
        {visibleArtifacts.map((artifact, idx) => (
          <div
            className="slide"
            key={`${artifact.id || 'artifact'}-${idx}-${Date.now()}`}
            ref={el => (slidesRef.current[idx] = el)}
            id={`slide-${idx}`}
          >
            {artifact.type === 'image' ? (
              <div className="image-content">
                <div className="image-left">
                  <img src={artifact.url} alt={`Artifact ${artifact.id || idx + 1}`} draggable={false} />
                </div>
                <div className="image-right">
                  {artifact.user_name && artifact.user_name.length > 0 && (
                    <div className="designer-name">{artifact.user_name}</div>
                  )}
                  {artifact.product_name && (
                    <div className="product-name">{artifact.product_name}</div>
                  )}
                  {artifact.product_category && (
                    <div className="product-category">{artifact.product_category}</div>
                  )}
                  {artifact.product_price && (
                    <div className="product-price">${artifact.product_price}</div>
                  )}
                  {artifact.product_description_first_paragraph && (
                    <div className="product-description">
                      {artifact.product_description_first_paragraph.length > 600 
                        ? `${artifact.product_description_first_paragraph.substring(0, 600)}...`
                        : artifact.product_description_first_paragraph
                      }
                    </div>
                  )}
                </div>
              </div>
            ) : artifact.type === 'instagram' ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                background: '#f9f9f9'
              }}>
                <img 
                  src={artifact.url} 
                  alt={`Instagram ${artifact.id || idx + 1}`} 
                  draggable={false}
                  style={{
                    maxWidth: '100vw',
                    maxHeight: '100vh',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    background: 'transparent'
                  }}
                />
              </div>
            ) : artifact.type === 'welcome' ? (
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                height: '100%',
                background: '#fff'
              }}>
                <img 
                  src={artifact.url} 
                  alt={`Instagram ${artifact.id || idx + 1}`} 
                  draggable={false}
                  style={{
                    maxWidth: '100vw',
                    maxHeight: '100vh',
                    width: 'auto',
                    height: 'auto',
                    objectFit: 'contain',
                    background: 'transparent'
                  }}
                />
              </div>
            ) : artifact.type === 'website' ? (
              <iframe 
                src={artifact.url} 
                title={`Website ${artifact.id || idx + 1}`}
                sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
                loading="lazy"
                style={{ 
                  width: '100%', 
                  height: '100%', 
                  border: 'none',
                  background: '#fff'
                }}
              />
            ) : (
              <video
                muted
                playsInline
                preload="auto"
                style={{ background: '#fff' }}
                tabIndex={-1}
              >
                <source src={artifact.url} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            )}
          </div>
        ))}
        {/* User name display for videos only - show if current artifact is video and has user_name */}
        {artifactQueue.length > 0 &&
          (artifactQueue[0].type === 'video' || artifactQueue[0].type === 'website') &&
          artifactQueue[0].user_name &&
          artifactQueue[0].user_name.length > 0 && (
            <div className="video-user-display">
              {artifactQueue[0].type === 'video'
                ? 'Directed by: '
                : artifactQueue[0].type === 'website'
                ? 'Imagined by: '
                : ''}
              {artifactQueue[0].user_name}
            </div>
        )}
        </div>
      </div>
    );
  };

export default ArtifactApp;
